import { computed, defineComponent, onMounted, ref, watch } from "@vue/composition-api";
import ReportTabLayout from "../report-tab-layout.vue";
import ReportToolbar from "../report-toolbar.vue";
import MaterialInput from "@Shared/components/inputs/material-input.vue";
import DateSection from "@Shared/components/sections/date-section.vue";
import Field from "@Shared/components/sections/field.vue";
import SelectLevel from "../select-level.vue";
import SelectFormat from "../select-format.vue";
import { useI18n } from "vue-i18n-composable";
import AgentsSelect from "@Broker/@Modules/agents/components/agents-select.vue";
import SelectApplicationStatus from "../select-application-status.vue";
import * as ReportsGetters from "../../store/reports-getters";
import * as AccountGetters from "@Broker/@Modules/main/store/account/account-getters";
import { useReportsStoreGetters } from "../../store/reports-helpers";
import { useAccountStoreGetters } from "@Broker/@Modules/main/store/account/account-helpers";
import { REPORT_DATE_TYPE_E, REPORT_LEVEL_E } from "@Broker/@Types/reports/types";
import { capitalize, cloneDeep } from "lodash";
import { validationMixin } from "vuelidate";
import { required, requiredIf } from "vuelidate/lib/validators";
import { useVuelidate } from "@Shared/@Composables/validations/useVuelidate";
import { useMultipleSelect } from "@Shared/@Composables/generic/useMultipleSelect";
import { useReportDefaultSettings } from "@Broker/@Composables/reports/useReportDefaultSettings";
import vSelectField from "@Shared/components/form-elements/fields/v-select-field.vue";
import RiskLevelLoader from "./risk-level-loader.vue";
import SelectRiskLevel from "../select-risk-level.vue";
import { REPORT_RISK_LEVEL_E } from "@Broker/@Types/reports/types";
export default defineComponent({
  name: "RiskLevelContainer",
  components: {
    ReportTabLayout,
    ReportToolbar,
    MaterialInput,
    DateSection,
    Field,
    SelectLevel,
    AgentsSelect,
    SelectApplicationStatus,
    SelectFormat,
    RiskLevelLoader,
    SelectRiskLevel,
    vSelectField
  },
  mixins: [validationMixin],
  props: {
    runningReport: {
      type: Object,
      default: () => ({
        loading: false,
        loadingSucceeded: true
      })
    }
  },
  validations() {
    return {
      riskLevelValues: {
        dateType: {
          required
        },
        dateFrom: {
          required
        },
        dateTo: {
          required
        },
        selectedLevel: {
          required
        },
        selectedAgents: {
          required: requiredIf(() => this.showAgentsSelect)
        },
        selectedApplicationStatus: {
          required
        },
        selectedFormat: {
          required
        },
        selectedRiskLevels: {
          required
        }
      }
    };
  },
  setup(props, {
    emit
  }) {
    const loading = ref(false);
    const {
      v$
    } = useVuelidate();
    const {
      t,
      tc
    } = useI18n();
    const {
      handleSelectMultipleChange
    } = useMultipleSelect();
    const unsyncedAppsModal = ref();
    const dateTypeOptions = computed(() => {
      return [{
        text: t("application-reports.risk-level.closingDate"),
        id: REPORT_DATE_TYPE_E.CLOSING_DATE
      }, {
        text: t("application-reports.risk-level.createdDate"),
        id: REPORT_DATE_TYPE_E.DATE_CREATED
      }, {
        text: t("application-reports.risk-level.acceptedDate"),
        id: REPORT_DATE_TYPE_E.ACCEPTED_DATE
      }];
    });
    const setDefaultButtonProps = ref({
      loading: false,
      loadingSucceeded: true
    });
    const allAgentsOption = {
      id: "all",
      username: "all",
      givenName: capitalize(tc("global.words.all", 1)),
      familyName: ""
    };
    const options = computed(() => ({
      placeholder: t("global.actions.chooseOption")
    }));
    const {
      CURRENT_ACCOUNT_IS_BROKER,
      CURRENT_USER_USERNAME
    } = useAccountStoreGetters([AccountGetters.CURRENT_ACCOUNT_IS_BROKER, AccountGetters.CURRENT_USER_USERNAME]);
    const {
      GET_RISK_LEVEL_STRUCTURE
    } = useReportsStoreGetters([ReportsGetters.GET_RISK_LEVEL_STRUCTURE]);
    const riskLevelStructure = cloneDeep(GET_RISK_LEVEL_STRUCTURE.value);
    const riskLevelValues = ref(riskLevelStructure);
    const showAgentsSelect = computed(() => {
      var _riskLevelValues$valu;
      return CURRENT_ACCOUNT_IS_BROKER.value && ((_riskLevelValues$valu = riskLevelValues.value) == null ? void 0 : _riskLevelValues$valu.selectedLevel) === REPORT_LEVEL_E.AGENT;
    });
    const dateRange = computed(() => {
      var _riskLevelValues$valu2, _riskLevelValues$valu3, _riskLevelValues$valu4, _riskLevelValues$valu5;
      return {
        start: (_riskLevelValues$valu2 = (_riskLevelValues$valu3 = riskLevelValues.value) == null ? void 0 : _riskLevelValues$valu3.dateFrom) != null ? _riskLevelValues$valu2 : "",
        end: (_riskLevelValues$valu4 = (_riskLevelValues$valu5 = riskLevelValues.value) == null ? void 0 : _riskLevelValues$valu5.dateTo) != null ? _riskLevelValues$valu4 : ""
      };
    });
    onMounted(() => {
      setDefaultAgentIfNeeded();
      setDefaultRiskLevels();
    });
    const setDefaultAgentIfNeeded = () => {
      if (!CURRENT_ACCOUNT_IS_BROKER.value) riskLevelValues.value.selectedAgents = [CURRENT_USER_USERNAME.value];
    };
    const setDefaultRiskLevels = () => {
      riskLevelValues.value.selectedRiskLevels = REPORT_RISK_LEVEL_E.ALL;
    };
    const handleChangeDates = range => {
      if (!riskLevelValues.value) return;
      riskLevelValues.value.dateFrom = range.start;
      riskLevelValues.value.dateTo = range.end;
    };
    const handleSelectAgentChange = (value, type) => {
      riskLevelValues.value.selectedAgents = handleSelectMultipleChange(value, type, riskLevelValues.value.selectedAgents, true);
    };
    const {
      loadReportDefaultValues,
      updateReportDefaults
    } = useReportDefaultSettings({
      reportType: "riskLevel",
      reportDataRef: riskLevelValues
    });
    const resetToDefaultValues = () => {
      loadReportDefaultValues();
      setDefaultAgentIfNeeded();
    };
    const setDefaultValues = async () => {
      setDefaultButtonProps.value.loading = true;
      const {
        selectedAgents: agents,
        selectedFormat: format,
        selectedLevel: level,
        dateFrom,
        dateTo,
        dateType,
        selectedApplicationStatus: applicationStatus,
        selectedRiskLevels: riskLevels
      } = riskLevelValues.value;
      await updateReportDefaults({
        agents,
        level,
        dateFrom,
        dateTo,
        applicationStatus,
        dateType,
        format,
        riskLevels
      });
      setDefaultButtonProps.value.loading = false;
    };
    const handleRunReport = async () => {
      emit("run-report", {
        reportType: "risk-level",
        data: riskLevelValues.value
      });
    };
    const handleClearSelection = () => {
      const clone = cloneDeep(riskLevelStructure);
      riskLevelValues.value = clone;
      setDefaultAgentIfNeeded();
    };
    watch(() => riskLevelValues.value.selectedLevel, (selectedLevel, oldSelectedLevel) => {
      if (selectedLevel === oldSelectedLevel) return;
      if (selectedLevel === REPORT_LEVEL_E.BROKERAGE) {
        riskLevelValues.value.selectedAgents = "all";
      }
    });
    return {
      dateTypeOptions,
      options,
      riskLevelValues,
      loading,
      dateRange,
      setDefaultButtonProps,
      handleRunReport,
      resetToDefaultValues,
      handleChangeDates,
      setDefaultValues,
      handleClearSelection,
      v$,
      showAgentsSelect,
      allAgentsOption,
      CURRENT_ACCOUNT_IS_BROKER,
      handleSelectAgentChange,
      setDefaultAgentIfNeeded,
      unsyncedAppsModal
    };
  }
});